// MDM GraphQL Queries
// All GraphQL queries related to mobile device management
import { gql } from '@apollo/client';

// Policy Attribute Meta Query
const GET_POLICY_ATTRIBUTE_META = gql`
  query getPolicyAttributeMeta($classId: ID!, $policyType: String) {
    getPolicyAttributeMeta(classId: $classId, policyType: $policyType)
  }
`;

const GET_POLICY_FOR_MDM = gql`
  query getPolicyForRestriction($policyId: ID!) {
    getPolicy(policyId: $policyId) {
      policyId
      name
      description
      enabled
      policyType
      mdmRestrictions {
        attributePath
        attributeDataType
        value
      }
      applePasscodePolicyConfig {
        configId
        allowSimple
        requireAlphanumeric
        maxFailedAttempts
        maxPINAgeInDays
        maxGracePeriod
        minComplexChars
        minLength
        pinHistory
        maxInactivity
      }
      androidPasswordPolicyConfig {
        configId
        passwordQuality
        passwordMinLength
        passwordHistoryLen
        enforceDailyLock
      }
      mdmAndroidOSUpdatePolicyConfig {
        configId
        updateType
        updateWindow {
          startTime
          endTime
        }
      }
      osUpdateSettings {
        notifications
        rapidSecurityResponse {
          enable
          enableRollback
        }
        automaticActions {
          download
          installOSUpdates
        }
        deferrals {
          combinedPeriodInDays
        }
        recommendedCadence
      }
      osUpdateEnforcement {
        enforcementType
        majorOsUpdateEnforcement {
          afterDays
          time
        }
        minorOsUpdateEnforcement {
          afterDays
          time
        }
        osUpdateVersionEnforcements {
          versionEnforcementId
          targetVersion
          targetLocalDateTime
        }
      }
    }
  }
`;

const GET_POLICY_FOR_ADE_PROFILE = gql`
  query getPolicyForADEProfile($policyId: ID!) {
    getPolicy(policyId: $policyId) {
      policyId
      name
      description
      enabled
      policyType
      appleADEProfilePolicyConfig {
        attributePath
        attributeDataType
        value
      }
    }
  }
`;

const GET_POLICY_TYPES_META = gql`
  query($classId: ID, $version: Int) {
    getPolicyTypesMeta(classId: $classId, version: $version)
  }
`;

const GET_ASSETCLASS_MODULES = gql`
  query getAssetClassModules($classId: ID) {
    getAssetClassModules(classId: $classId)
  }
`;

const GET_MDM_DEVICE_COMMANDS_META = gql`
  query getMDMDeviceCommandsMeta($assetId: ID!) {
    getMDMDeviceCommandsMeta(assetId: $assetId)
  }
`;

// Query to get WiFi policy configuration
const GET_WIFI_POLICY = gql`
  query getPolicyForWifi($policyId: ID!) {
    getPolicy(policyId: $policyId) {
      policyId
      name
      description
      enabled
      policyType
      mdmWifiPolicyConfig {
        configId
        ssid
        encryptionType
        wifiPassword
        autoJoin
        disableRandomMacAddress
        enableIPv6
        hidden
        hotspot
        wifiProxyType
        proxyPACFallbackAllowed
        proxyPACURL
        proxyServerHost
        proxyServerPort
        proxyUserName
        proxyPassword
      }
    }
  }
`;

// Query to get Proxy policy configuration
const GET_PROXY_POLICY = gql`
  query getPolicyForProxy($policyId: ID!) {
    getPolicy(policyId: $policyId) {
      policyId
      name
      description
      enabled
      policyType
      mdmProxyPolicyConfig {
        configId
        proxyType
        host
        port
        userName
        password
        pacURL
        pacFallbackAllowed
        proxyCaptiveLoginAllowed
      }
    }
  }
`;

export const EXECUTE_ASSETS_ACTION = gql`
  mutation executeMDMCommands(
    $commandName: String!
    $assetIds: [ID]!
    $actionParams: JSON
  ) {
    executeMDMCommands(
      commandName: $commandName
      assetIds: $assetIds
      actionParams: $actionParams
    )
  }
`;

const GET_ENTITY_META = gql`
  query getEntitiesMeta($entityNames: [String!]!) {
    getEntitiesMeta(entityNames: $entityNames) {
      label
      name
      attributes {
        name
        label
        attributePlaceHolder {
          key
          label
        }
        filterType
        isIdentifier
        isKeyAttribute
        isDependent
        dataType
        entity
        uiDataType
        conditionPlaceHolders {
          key
          label
        }
        isCustom
        nestedAttributes
        nestedTo
        showToClient
        autoComplete
        useInMetric
        metricFunctions
        metricUnits
        labelInMetric
        useInGroupBy
        useInCondition
        conditionPlaceHoldersMeta
      }
    }
  }
`;

const GET_ASSET_THIRD_PARTY_APPS_QUERY = gql`
  query($assetId: ID!, $listInfo: ListInfoInput!) {
    getAssetThirdPartyApplicationsList(assetId: $assetId, listInfo: $listInfo) {
      assetThirdPartyApplications {
        thirdPartyApplication {
          name
          id
          applicationReferenceId
          title
          author
          iconUrl
          thirdPartyPackageManager {
            pmId
            packageManager
          }
          packageType
        }
        status
        configuredInPolicy
        updateAvailable
        lastManagedBy
      }
      listInfo {
        page
        pageSize
        hasMore
        totalCount
        condition
        sort {
          attribute
          order
        }
      }
    }
  }
`;

const GET_ASSET_ANDROID_SOFTWARE_INVENTORY_QUERY = gql`
  query($assetId: ID!, $listInfo: ListInfoInput!) {
    getAssetAndroidSoftwareInventory(assetId: $assetId, listInfo: $listInfo) {
      androidSoftwares {
        id
        assetId
        packageName
        displayName
        version
        installedDate
        applicationSource
        state
      }
      listInfo {
        page
        pageSize
        hasMore
        totalCount
        condition
        sort {
          attribute
          order
        }
      }
    }
  }
`;

export { GET_APPLE_SOFTWARE_INVENTORY_QUERY } from './MDMSettingsPage';

const GET_AVAILABLE_OS_UPDATE_FOR_APPLE = gql`
  query getAvailableOSUpdateForApple($classId: ID!) {
    getSupportedOSUpdatesForApple(classId: $classId) {
      productVersion
      build
      postingDate
      expirationDate
    }
  }
`;

export {
  GET_POLICY_TYPES_META,
  GET_ASSETCLASS_MODULES,
  GET_POLICY_ATTRIBUTE_META,
  GET_MDM_DEVICE_COMMANDS_META,
  GET_POLICY_FOR_MDM,
  GET_ENTITY_META,
  GET_POLICY_FOR_ADE_PROFILE,
  GET_WIFI_POLICY,
  GET_PROXY_POLICY,
  GET_ASSET_THIRD_PARTY_APPS_QUERY,
  GET_ASSET_ANDROID_SOFTWARE_INVENTORY_QUERY,
  GET_AVAILABLE_OS_UPDATE_FOR_APPLE
};
